import { InputAdornment, styled, SvgIcon, TextField } from "@mui/material";
import React, { useEffect, useState } from "react";
import { mdiCalendarMonth } from "@mdi/js";
import moment from "moment";
import { generateDirStyles } from "orion-components/SharedComponents/GenerateDirStyles/GenerateDirStyles";
import { useSelector } from "react-redux";
import { getDir } from "orion-components/i18n/Config/selector";
import { getTranslation } from "orion-components/i18n/Actions/index.js";

const CustomizedTextField = styled(TextField)(() => ({
	"& .MuiOutlinedInput-notchedOutline": {
		border: "1px solid #707070",
		width: 100,
		height: 28,
		borderRadius: 5,
		opacity: 1
	},

	"& .MuiOutlinedInput-root": {
		"&:hover fieldset": {
			borderColor: "#4DB5F4"
		},
		"&.Mui-focused fieldset": {
			borderColor: "#4DB5F4"
		},

		width: 100,
		height: 28,
		fontWeight: "normal",
		fontFamily: "Roboto",
		letterSpacing: "0px",
		opacity: 1,
		fontSize: 12,
		color: "#B5B9BE",
		paddingRight: "8px"
	},

	"& .MuiInputBase-input": {
		width: "100%",
		padding: "0 6px",
		height: "100%",
		marginBottom: 4,
		"&::placeholder": {
			fontSize: 9
		}
	},

	"& .MuiInputAdornment-root": {
		margin: "0",
		padding: "0"
	}
}));

const baseStyles = {
	inputAdornment: {
		"&.MuiInputAdornment-root": {
			position: "absolute !important",
			right: "0 !important"
		}
	},
	dateIconWrapper: {
		backgroundColor: "grey",
		borderRadius: "4px",
		display: "flex",
		alignItems: "center",
		justifyContent: "center",
		width: 22,
		height: 22,
		cursor: "pointer",
		marginBottom: 5
	},
	dateIcon: { width: 18, height: 18, color: "#FFFFFF" }
};

const DateInput = ({ setShowDatePicker, datePickerDate, sendValuesToParent, error }) => {
	const dir = useSelector((state) => getDir(state));

	const styles = generateDirStyles(baseStyles, dir);

	const formatDate = (date) => {
		const validDate = moment(date);
		if (!validDate.isValid()) {
			sendValuesToParent(
				"error",
				getTranslation(
					"global.CBComponents.newVideoPlayer.components.recorded.jumpToDifferentTime.components.dateInput.invalidDate"
				)
			);
			return "";
		}
		return validDate.format("MM/DD/YYYY");
	};

	// Function to get the current date in MM/DD/YYYY format
	const getCurrentDate = () => {
		const today = new Date();
		const month = String(today.getMonth() + 1).padStart(2, "0"); // Get month (0-based index)
		const day = String(today.getDate()).padStart(2, "0"); // Get day
		const year = today.getFullYear();
		return `${month}/${day}/${year}`;
	};

	const [date, setDate] = useState(getCurrentDate());

	useEffect(() => {
		setDate(formatDate(datePickerDate));
	}, [datePickerDate]);

	// Function to validate date
	const validateDate = (value) => {
		const parts = value.split("/");
		if (parts.length !== 3 || value.length < 10) {
			sendValuesToParent(
				"error",
				getTranslation(
					"global.CBComponents.newVideoPlayer.components.recorded.jumpToDifferentTime.components.dateInput.invalidDate"
				)
			);
			return false;
		}

		const [month, day, year] = parts.map((num) => parseInt(num, 10));

		// Validate month
		if (month < 1 || month > 12) {
			sendValuesToParent(
				"error",
				getTranslation(
					"global.CBComponents.newVideoPlayer.components.recorded.jumpToDifferentTime.components.dateInput.invalidDate"
				)
			);
			return false;
		}

		// Validate day based on month
		const daysInMonth = new Date(year, month, 0).getDate(); // Get days in given month
		if (day < 1 || day > daysInMonth) {
			sendValuesToParent(
				"error",
				getTranslation(
					"global.CBComponents.newVideoPlayer.components.recorded.jumpToDifferentTime.components.dateInput.invalidDate"
				)
			);
			return false;
		}

		sendValuesToParent("error", "");
		return true;
	};

	// Function to handle input change
	const handleChange = (e) => {
		const inputElement = e.target;
		const cursorPosition = inputElement.selectionStart;

		let input = inputElement.value.replace(/\D/g, ""); // Remove non-numeric characters

		if (input.length > 8) {
			return;
		}

		// Auto-format with slashes (MM/DD/YYYY)
		if (input.length >= 2) {
			input = input.slice(0, 2) + "/" + input.slice(2);
		}
		if (input.length >= 5) {
			input = input.slice(0, 5) + "/" + input.slice(5);
		}

		setDate(input);

		// Restore cursor position after state update
		requestAnimationFrame(() => {
			inputElement.setSelectionRange(cursorPosition, cursorPosition);
		});

		validateDate(input);
	};

	const synchronizeWithDatePicker = () => {
		if (error === "") {
			const momentDate = moment(date, "MM/DD/YYYY").toDate();
			sendValuesToParent("date", momentDate);
		}
	};

	return (
		<div>
			<CustomizedTextField
				value={date}
				onChange={handleChange}
				placeholder="MM/DD/YYYY"
				inputProps={{
					autoComplete: "off"
				}}
				onBlur={synchronizeWithDatePicker}
				InputProps={{
					endAdornment: (
						<InputAdornment position="end" sx={styles.inputAdornment}>
							<div style={styles.dateIconWrapper}>
								<SvgIcon
									style={styles.dateIcon}
									onClick={() => {
										setShowDatePicker(true);
										synchronizeWithDatePicker();
									}} data-testid="orion-components-cbcomponents-new-video-player-components-recorded-jump-to-different-time-components-date-input-svg-icon"
								>
									<path d={mdiCalendarMonth} />
								</SvgIcon>
							</div>
						</InputAdornment>
					)
				}} data-testid="orion-components-cbcomponents-new-video-player-components-recorded-jump-to-different-time-components-date-input-customized-text-field"
			/>
		</div>
	);
};

export default DateInput;
